// Metadata UNO sdílená serverem i klientem: karty, české názvy, typy pohledu a akcí. Žádná logika.

export type Color = "r" | "y" | "g" | "b";
export type Kind = "num" | "skip" | "reverse" | "draw2" | "wild" | "wild4";

export interface Card {
  id: number;
  /** null = černá karta (změna barvy, +4). */
  color: Color | null;
  kind: Kind;
  value?: number;
}

export const COLORS: Color[] = ["r", "y", "g", "b"];

export const COLOR_NAME: Record<Color, string> = { r: "červená", y: "žlutá", g: "zelená", b: "modrá" };

/** Barva v 4. pádě („vybral modrou“). */
export const COLOR_ACC: Record<Color, string> = { r: "červenou", y: "žlutou", g: "zelenou", b: "modrou" };

const NUMBER_NAME = ["nula", "jednička", "dvojka", "trojka", "čtyřka", "pětka", "šestka", "sedmička", "osmička", "devítka"];

const KIND_NAME: Record<Exclude<Kind, "num">, string> = {
  skip: "stop",
  reverse: "otočka",
  draw2: "plus dva",
  wild: "změna barvy",
  wild4: "plus čtyři",
};

export function cardName(card: Card): string {
  const what = card.kind === "num" ? NUMBER_NAME[card.value ?? 0] : KIND_NAME[card.kind];
  return card.color ? `${COLOR_NAME[card.color]} ${what}` : what;
}

/** Celý balíček: 108 karet podle oficiálních pravidel. */
export function fullDeck(): Card[] {
  const cards: Omit<Card, "id">[] = [];
  for (const color of COLORS) {
    cards.push({ color, kind: "num", value: 0 });
    for (let v = 1; v <= 9; v++) cards.push({ color, kind: "num", value: v }, { color, kind: "num", value: v });
    for (const kind of ["skip", "reverse", "draw2"] as const) cards.push({ color, kind }, { color, kind });
  }
  for (let i = 0; i < 4; i++) cards.push({ color: null, kind: "wild" }, { color: null, kind: "wild4" });
  return cards.map((c, id) => ({ ...c, id }));
}

export const HAND_SIZE = 7;

/** Jde kartu zahrát? Sdílí server (pravidla) i klient (nápověda „hratelná“). */
export function isPlayable(card: Card, hand: Card[], top: Card, color: Color, pendingDraw: number, stacking: boolean): boolean {
  if (pendingDraw > 0) return stacking && card.kind === "draw2";
  if (card.kind === "wild") return true;
  // +4 jen když v ruce není karta aktuální barvy (oficiální pravidlo, bez zpochybnění).
  if (card.kind === "wild4") return !hand.some((c) => c.id !== card.id && c.color === color);
  if (card.color === color) return true;
  if (card.kind === "num") return top.kind === "num" && top.value === card.value;
  return top.kind === card.kind;
}

export interface UnoView {
  /** Moje karty (divák nemá žádné). */
  hand: Card[];
  players: { id: string; name: string; cards: number }[];
  top: Card;
  /** Platná barva (u černé karty ta zvolená). */
  color: Color;
  turn: string;
  direction: 1 | -1;
  drawPile: number;
  /** Kolik karet čeká na líznutí při hromadění +2 (0 = nic). */
  pendingDraw: number;
  /** Po líznutí: tuhle kartu smím zahrát, nebo tah předat. Jen pro hráče na tahu. */
  drawnCardId: number | null;
  stacking: boolean;
  over: boolean;
  winners: string[];
}

export type UnoAction =
  | { type: "play"; cardId: number; color?: Color }
  | { type: "draw" }
  | { type: "pass" };
